/**
 * Which conversation the canvas is showing (TASK-795).
 *
 * The canvas placement was the last surface where TASK-769's promise —
 * *placement is presentation, thread is state* — did not hold. Its conversation
 * lived in one unscoped singleton (`dashboardDockStore`), so it could not
 * represent "this conversation belongs to task X" at all, and TASK-788 had to
 * suppress task threads here rather than fix them.
 *
 * Both `Canvas` and `InputDock` need the answer — one to decide which bubbles
 * to render, the other to decide which store to write — so it is derived HERE
 * once. Two components resolving the same scope independently is how they drift.
 */

import { FEATURES } from '../../config/api-config';
import { threadScope, type ActiveTaskThread, type DockScope } from '../dock/dockScope';

/**
 * The canvas placement. `dashboard` is already a `ChatScopeType`; it names no
 * entity, which is exactly why a task thread may win here — TASK-787's rule is
 * that a placement gives way only when it is about the task's own client or
 * about nothing at all. The dashboard is the "nothing at all" case.
 */
export const CANVAS_PLACEMENT: DockScope = {
  scopeType: 'dashboard',
  entityId: null,
  label: 'Dashboard',
};

/** Stable key for "which thread is this", used to attribute canvas chat tiles. */
export const threadKeyForScope = (scope: DockScope): string =>
  `${scope.scopeType}:${scope.entityId ?? 'global'}`;

/**
 * The thread key of the canvas's own conversation.
 *
 * Chat tiles created before TASK-795 carry no `threadKey`, and tiles restored
 * from a saved workstream may not either. They default to this: the dashboard
 * thread was the only one that existed, so attributing them anywhere else would
 * be inventing a history.
 */
export const CANVAS_BASE_THREAD_KEY = threadKeyForScope(CANVAS_PLACEMENT);

/**
 * Placement + thread → the scope whose store holds this turn. The same
 * composition `Dock.tsx` performs; the flag gate is what makes
 * `ENABLE_TASK_THREADS=false` a real rollback on this surface too.
 *
 * `meetingPopupId` is the focus-stack meeting popup, if one is in front
 * (TASK-714/715). It matters because the canvas has TWO axes — the thread and
 * the popup — and only the thread axis passes through `threadScope`. Without
 * this, a meeting popup for client B opened while client A's task thread is
 * active would commit B's meeting conversation into `task:<A's task>`, which is
 * exactly the cross-client contamination TASK-787's allowlist exists to stop.
 * The rule is not restated here: the popup is expressed as a placement that
 * NAMES an entity and the real `threadScope` is asked, so this surface cannot
 * drift from the dock's answer.
 */
export const canvasScope = (
  activeTask: ActiveTaskThread | null,
  meetingPopupId?: string | null
): DockScope => {
  if (!FEATURES.ENABLE_TASK_THREADS) return CANVAS_PLACEMENT;
  const placement: DockScope = meetingPopupId
    ? { ...CANVAS_PLACEMENT, entity: { type: 'meeting', id: meetingPopupId } }
    : CANVAS_PLACEMENT;
  const resolved = threadScope(placement, activeTask);
  // The popup keeps its own store (TASK-715), so a refusal means the canvas
  // stays on its own placement rather than adopting the popup's identity.
  return resolved.scopeType === 'task' ? resolved : CANVAS_PLACEMENT;
};

/**
 * Does this tile belong to the conversation currently on screen?
 *
 * DEC-6: only chat tiles are attributed. Widget tiles are the advisor's
 * arranged WORK — they survive a thread switch, because clearing a grid someone
 * built to solve a bookkeeping problem is not a trade worth making.
 */
export const tileBelongsToThread = (
  tile: { component: string; threadKey?: string },
  activeThreadKey: string
): boolean =>
  tile.component !== 'ChatMessage' ||
  (tile.threadKey ?? CANVAS_BASE_THREAD_KEY) === activeThreadKey;
